import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { styled } from '@mui/material';

import { Icon, Menu } from '@components/base';
import { menuItemClasses } from '@components/base/navigation/Menu/utils';
import env from '@config/runtimeConfig';
import { AppContext, appUrlMap } from 'src/utils/envUtils';

import { ActionButton } from './helperComponents';

const StyledMenuItem = styled(Menu.Item)`
  .${menuItemClasses.rightContent} {
    opacity: 0;
  }

  &:hover,
  &:focus-visible {
    .${menuItemClasses.rightContent} {
      opacity: 0.7;
    }
  }
`;

const uuidRegexp =
  /[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

const EnvSelector: FC = () => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();

  const currentUrlContext: AppContext | undefined = Object.entries(appUrlMap).find(([_, urls]) =>
    Object.values(urls).some(basePath => window.location.href.startsWith(basePath))
  )?.[0] as AppContext;

  const hasUuid = uuidRegexp.test(pathname);
  const newPathname = new RegExp(`^(.*?)${uuidRegexp.source}`).exec(pathname)?.[1] ?? pathname;

  return currentUrlContext ? (
    <Menu>
      <Menu.Button color='primary' component={ActionButton} leftIcon='flag' variant='contained'>
        {t('env', { context: env })}
      </Menu.Button>

      <Menu.Content
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {Object.entries(appUrlMap[currentUrlContext]).map(([environment, url]) => (
          <StyledMenuItem
            key={environment}
            {...(env === environment
              ? {
                  icon: <Icon color='secondary' name='check' />,
                  selected: true,
                }
              : {
                  rightContent: <Icon fontSize='small' name='openInNew' />,
                  icon: <span />,
                  target: '_blank',
                  to: `${url}${newPathname}${hasUuid ? '' : search}`,
                })}
          >
            {t('env', { context: environment })}
          </StyledMenuItem>
        ))}
      </Menu.Content>
    </Menu>
  ) : null;
};

export default EnvSelector;
